import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import Checkbox from '@/components/atoms/Checkbox';
import type { PenRef } from '@/domain/providerConfig';

import styles from './ProviderCard.module.scss';

export interface TestControlsProps {
    /** The pens the save leaves connected; only those can be tested. */
    candidates: PenRef[];
    selected: number[];
    onSelectedChange: (cageIds: number[]) => void;
    onRun: () => void;
    testing: boolean;
    /** A required value for the tested pens is empty, so the test could only say so. */
    missingRequired: boolean;
    needsTest: boolean;
    tokenValid: boolean;
    /** OptoScale admins only, for a provider that is down. */
    showOverride: boolean;
    testOverride: boolean;
    onTestOverrideChange: (value: boolean) => void;
}

function TestControls({
    candidates,
    selected,
    onSelectedChange,
    onRun,
    testing,
    missingRequired,
    needsTest,
    tokenValid,
    showOverride,
    testOverride,
    onTestOverrideChange,
}: TestControlsProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');

    function toggle(cageId: number, checked: boolean): void {
        onSelectedChange(
            checked ? [...selected, cageId] : selected.filter((other) => other !== cageId)
        );
    }

    return (
        <div className={styles.testControls}>
            {candidates.length > 0 && (
                <fieldset className={styles.testPens}>
                    <legend className={styles.legend}>{t('providerConfig.test.pens')}</legend>
                    {candidates.map((pen) => (
                        <Checkbox
                            key={pen.cageId}
                            id={`${id}-pen-${pen.cageId}`}
                            label={pen.name}
                            checked={selected.includes(pen.cageId)}
                            disabled={testing}
                            onCheckedChange={(checked) => toggle(pen.cageId, checked)}
                        />
                    ))}
                </fieldset>
            )}
            <div className={styles.testRow}>
                <Button
                    variant="secondary"
                    disabled={testing || selected.length === 0 || missingRequired}
                    onClick={onRun}>
                    {t('providerConfig.test.run')}
                </Button>
                {missingRequired ? (
                    <p className={styles.note}>{t('providerConfig.test.fillRequired')}</p>
                ) : (
                    needsTest &&
                    !tokenValid && (
                        <p className={styles.note}>{t('providerConfig.test.required')}</p>
                    )
                )}
            </div>
            {showOverride && needsTest && !tokenValid && (
                <Checkbox
                    id={`${id}-override`}
                    label={t('providerConfig.test.override')}
                    checked={testOverride}
                    onCheckedChange={onTestOverrideChange}
                />
            )}
        </div>
    );
}

export default TestControls;
export { TestControls };
